import { auth, db, storage } from "../firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable
} from "firebase/storage";

export const BUILD_DOCUMENT_CATEGORIES = [
  "Maintenance",
  "Mods",
  "Warranty",
  "Other"
];

export const MAX_BUILD_DOCUMENT_SIZE = 10 * 1024 * 1024;

export const ALLOWED_BUILD_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png"
];

export const validateBuildDocumentFile = (file) => {
  if (!file) {
    throw new Error("Please choose a document to upload.");
  }

  if (file.size > MAX_BUILD_DOCUMENT_SIZE) {
    throw new Error("Document must be 10 MB or smaller.");
  }

  if (!ALLOWED_BUILD_DOCUMENT_TYPES.includes(file.type)) {
    throw new Error("Only PDF, JPEG, and PNG files are allowed.");
  }

  return true;
};

export const sanitizeBuildDocumentFileName = (fileName = "") => {
  const rawName = String(fileName || "")
    .replace(/[/\\]/g, "-")
    .replace(/\.+/g, ".")
    .trim();

  const lastDotIndex = rawName.lastIndexOf(".");
  const hasSafeExtension =
    lastDotIndex > 0 && lastDotIndex < rawName.length - 1;

  const rawBaseName = hasSafeExtension
    ? rawName.slice(0, lastDotIndex)
    : rawName;

  const rawExtension = hasSafeExtension
    ? rawName.slice(lastDotIndex + 1)
    : "";

  const safeBaseName =
    rawBaseName
      .replace(/\.\./g, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^[-_.]+|[-_.]+$/g, "") || "document";

  const safeExtension = rawExtension
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

  return safeExtension ? `${safeBaseName}.${safeExtension}` : safeBaseName;
};

export const formatBuildDocumentFileSize = (size) => {
  const numericSize = Number(size);

  if (!numericSize || Number.isNaN(numericSize) || numericSize <= 0) {
    return "0 Bytes";
  }

  const units = ["Bytes", "KB", "MB", "GB"];
  let value = numericSize;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const formattedValue =
    unitIndex === 0
      ? String(Math.round(value))
      : String(Number(value.toFixed(2)));

  return `${formattedValue} ${units[unitIndex]}`;
};

const normalizeBuildDocumentLink = ({
  linkedType = null,
  linkedItemId = "",
  linkedItemTitle = ""
} = {}) => {
  let normalizedLinkedType = linkedType;

  if (
    normalizedLinkedType === "" ||
    normalizedLinkedType === undefined ||
    normalizedLinkedType === null
  ) {
    normalizedLinkedType = null;
  }

  if (
    normalizedLinkedType !== null &&
    normalizedLinkedType !== "maintenance" &&
    normalizedLinkedType !== "mod"
  ) {
    throw new Error("Document link type must be maintenance, mod, or empty.");
  }

  let normalizedLinkedItemId =
    linkedItemId === null || linkedItemId === undefined
      ? ""
      : String(linkedItemId).trim();

  let normalizedLinkedItemTitle =
    linkedItemTitle === null || linkedItemTitle === undefined
      ? ""
      : String(linkedItemTitle).trim();

  if (normalizedLinkedType === "maintenance" || normalizedLinkedType === "mod") {
    if (!normalizedLinkedItemId) {
      throw new Error("Choose an item to link this document to.");
    }

    if (!normalizedLinkedItemTitle) {
      throw new Error("Linked item title is required.");
    }
  }

  if (normalizedLinkedType === null) {
    normalizedLinkedItemId = "";
    normalizedLinkedItemTitle = "";
  }

  return {
    linkedType: normalizedLinkedType,
    linkedItemId: normalizedLinkedItemId,
    linkedItemTitle: normalizedLinkedItemTitle
  };
};

const deleteUploadedObjectForCleanup = async (storageRef) => {
  try {
    await deleteObject(storageRef);
  } catch (cleanupError) {
    if (cleanupError?.code === "storage/object-not-found") {
      return;
    }

   console.error("Build Binder storage cleanup failed:", cleanupError);
  }
};

export const fetchBuildDocuments = async ({ ownerId, carId }) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("You must be logged in to view Build Binder documents.");
  }

  if (!ownerId || !carId) {
    throw new Error("Missing Build Binder vehicle information.");
  }

  if (currentUser.uid !== ownerId) {
    throw new Error("You do not have permission to view these documents.");
  }

  const documentsQuery = query(
    collection(db, "buildDocuments"),
    where("ownerId", "==", ownerId),
    where("carId", "==", String(carId)),
    orderBy("createdAt", "desc")
  );

  const snapshot = await getDocs(documentsQuery);

  return snapshot.docs.map((documentSnapshot) => ({
    id: documentSnapshot.id,
    ...documentSnapshot.data()
  }));
};

export const uploadBuildDocument = async ({
  ownerId,
  username,
  carId,
  title,
  category,
  description,
  documentDate,
  file,
  linkedType = null,
  linkedItemId = "",
  linkedItemTitle = "",
  onProgress
}) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("You must be logged in to upload Build Binder documents.");
  }

  const safeOwnerId = String(ownerId || "").trim();
  const safeUsername = String(username || "").trim();
  const safeCarId = String(carId || "").trim();
  const safeTitle = String(title || "").trim();
  const safeCategory = String(category || "").trim();
  const safeDocumentDate = String(documentDate || "").trim();

  if (!safeOwnerId || currentUser.uid !== safeOwnerId) {
    throw new Error("You do not have permission to upload this document.");
  }

  if (!safeUsername || !safeCarId) {
    throw new Error("Missing Build Binder vehicle information.");
  }

  if (!safeTitle) {
    throw new Error("Enter a document title.");
  }

  if (!safeCategory) {
    throw new Error("Choose a document category.");
  }

  if (!BUILD_DOCUMENT_CATEGORIES.includes(safeCategory)) {
    throw new Error("Choose a valid document category.");
  }

  if (!safeDocumentDate) {
    throw new Error("Choose a document date.");
  }

  validateBuildDocumentFile(file);

  const normalizedLink = normalizeBuildDocumentLink({
    linkedType,
    linkedItemId,
    linkedItemTitle
  });

  const documentRef = doc(collection(db, "buildDocuments"));
  const documentId = documentRef.id;
  const sanitizedFileName = sanitizeBuildDocumentFileName(file.name);
  const storagePath = `buildBinder/${safeOwnerId}/${safeCarId}/${documentId}/${sanitizedFileName}`;
  const storageRef = ref(storage, storagePath);

  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type
  });

  await new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (typeof onProgress === "function") {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );

          onProgress(progress);
        }
      },
      reject,
      resolve
    );
  });

  if (typeof onProgress === "function") {
    onProgress(100);
  }

  let downloadUrl = "";

  try {
    downloadUrl = await getDownloadURL(storageRef);
  } catch (error) {
    await deleteUploadedObjectForCleanup(storageRef);
    throw error;
  }

  const metadata = {
    ownerId: safeOwnerId,
    username: safeUsername,
    carId: safeCarId,
    title: safeTitle,
    category: safeCategory,
    description: String(description || "").trim(),
    documentDate: safeDocumentDate,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    storagePath,
    downloadUrl,
    linkedType: normalizedLink.linkedType,
    linkedItemId: normalizedLink.linkedItemId,
    linkedItemTitle: normalizedLink.linkedItemTitle,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  try {
    await setDoc(documentRef, metadata);
  } catch (error) {
    await deleteUploadedObjectForCleanup(storageRef);
    throw error;
  }

  return {
    id: documentId,
    ...metadata
  };
};

export const updateBuildDocumentLink = async ({
  documentId,
  ownerId,
  linkedType = null,
  linkedItemId = "",
  linkedItemTitle = ""
}) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("You must be logged in to update Build Binder documents.");
  }

  const safeOwnerId = String(ownerId || "").trim();
  const safeDocumentId = String(documentId || "").trim();

  if (!safeOwnerId || currentUser.uid !== safeOwnerId) {
    throw new Error("You do not have permission to update this document.");
  }

  if (!safeDocumentId) {
    throw new Error("Missing document ID.");
  }

  const normalizedLink = normalizeBuildDocumentLink({
    linkedType,
    linkedItemId,
    linkedItemTitle
  });

  await updateDoc(doc(db, "buildDocuments", safeDocumentId), {
    linkedType: normalizedLink.linkedType,
    linkedItemId: normalizedLink.linkedItemId,
    linkedItemTitle: normalizedLink.linkedItemTitle,
    updatedAt: serverTimestamp()
  });

  return normalizedLink;
};

export const deleteBuildDocument = async (documentItem) => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("You must be logged in to delete Build Binder documents.");
  }

  if (!documentItem?.id) {
    throw new Error("Missing document ID.");
  }

  if (!documentItem.ownerId) {
    throw new Error("Missing document owner.");
  }

  if (currentUser.uid !== documentItem.ownerId) {
    throw new Error("You do not have permission to delete this document.");
  }

  if (documentItem.storagePath) {
    const fileRef = ref(storage, documentItem.storagePath);

    try {
      await deleteObject(fileRef);
    } catch (error) {
      if (error?.code !== "storage/object-not-found") {
        throw error;
      }
    }
  }

  await deleteDoc(doc(db, "buildDocuments", documentItem.id));

  return true;
};